/**
 * Deviation Threshold Service – PRD §3.5
 * Configurable thresholds stored in DB. Contextual: referenceValue × contextMultiplier.
 * Resolution order: standard_requirement → requirement → standard (process) → organization → system defaults.
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import processService from './processService';
import qmsPolicyService from './qmsPolicyService';
import qmsStandardService from './qmsStandardService';

const COLLECTION = 'deviation_thresholds';

const DEFAULTS = {
  timing: {
    varianceTriggerPct: 50,      // variance > 50% → deviation
    varianceMajorPct: 80,        // variance > 80% → major severity
    complianceImpactMinor: 5,
    complianceImpactMajor: 10
  },
  sequence: {
    severity: 'major',
    complianceImpact: 15
  },
  quality: {
    severity: 'minor',
    complianceImpact: 5
  },
  risk: {
    rpnThreshold: 500,           // RPN >= 500 = high-risk
    severity: 'critical',
    complianceImpact: 20,
    rpnImpact: 50
  }
};

export class DeviationThresholdService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  /**
   * Get thresholds for organization, optionally scoped to standard/requirement.
   * Resolution order: standard_requirement → requirement → standard (process) → organization → system defaults.
   * @param {string} organizationId
   * @param {{ standardId?: string, requirementId?: string, standardRequirementId?: string }} [scope]
   */
  async getThresholds(organizationId, scope = {}) {
    const { standardId, requirementId, standardRequirementId } = scope;
    let merged = { ...DEFAULTS };

    // 1. Organization-level
    if (organizationId) {
      const q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        where('isActive', '==', true)
      );
      const snap = await getDocs(q);
      const orgConfig = snap.docs[0]?.data();
      if (orgConfig?.thresholds) {
        merged = this._deepMerge(merged, orgConfig.thresholds);
      }
    }

    // 2. Standard (process) level
    if (standardId) {
      try {
        const proc = await processService.getProcess(standardId);
        if (proc?.deviationThresholds && typeof proc.deviationThresholds === 'object') {
          merged = this._deepMerge(merged, proc.deviationThresholds);
        }
      } catch (_) {}
    }

    // 3. Requirement level
    if (requirementId) {
      try {
        const req = await qmsPolicyService.getRequirement(requirementId);
        if (req?.deviationThresholds && typeof req.deviationThresholds === 'object') {
          merged = this._deepMerge(merged, req.deviationThresholds);
        }
      } catch (_) {}
    }

    // 4. Standard-requirement link level (most specific)
    if (standardRequirementId) {
      try {
        const sr = await qmsStandardService.getStandardRequirement(standardRequirementId);
        if (sr?.deviationThresholds && typeof sr.deviationThresholds === 'object') {
          merged = this._deepMerge(merged, sr.deviationThresholds);
        }
      } catch (_) {}
    }

    return merged;
  }

  _deepMerge(target, source) {
    if (!source || typeof source !== 'object') return target;
    const result = { ...target };
    for (const [key, value] of Object.entries(source)) {
      if (value != null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[key] = this._deepMerge(result[key] || {}, value);
      } else if (value !== undefined) {
        result[key] = value;
      }
    }
    return result;
  }

  /**
   * Get timing threshold values for variance check.
   */
  async getTimingThresholds(organizationId, scope) {
    const t = await this.getThresholds(organizationId, scope);
    return t.timing || DEFAULTS.timing;
  }

  /**
   * Get risk RPN threshold for high-risk deviation.
   */
  async getRiskRpnThreshold(organizationId, scope) {
    const t = await this.getThresholds(organizationId, scope);
    return t.risk?.rpnThreshold ?? DEFAULTS.risk.rpnThreshold;
  }

  /**
   * Check if variance exceeds configured timing threshold.
   */
  async exceedsTimingThreshold(organizationId, variance, scope) {
    const timing = await this.getTimingThresholds(organizationId, scope);
    return variance > (timing.varianceTriggerPct / 100);
  }

  /**
   * Get severity for timing deviation based on variance.
   */
  async getTimingSeverity(organizationId, variance, scope) {
    const timing = await this.getTimingThresholds(organizationId, scope);
    const majorPct = (timing.varianceMajorPct ?? 80) / 100;
    return variance > majorPct ? 'major' : 'minor';
  }

  /**
   * Save or update organization thresholds.
   */
  async saveThresholds(organizationId, thresholds, userId) {
    const now = Timestamp.now();
    const q = query(
      this.collectionRef,
      where('organizationId', '==', organizationId)
    );
    const snap = await getDocs(q);
    const existing = snap.docs[0];

    const payload = {
      organizationId,
      thresholds: { ...DEFAULTS, ...thresholds },
      isActive: true,
      updatedAt: now,
      updatedBy: userId
    };

    if (existing) {
      await updateDoc(doc(this.collectionRef, existing.id), payload);
      return existing.id;
    }
    const ref = await addDoc(this.collectionRef, {
      ...payload,
      createdAt: now,
      createdBy: userId
    });
    return ref.id;
  }
}

export const deviationThresholdService = new DeviationThresholdService();
